import { Command, Editor, Element } from "ckeditor5";

import { isListItemBlock } from "../../_nonpublic/list/model";
import { collectAncestors, collectDescendents } from "../../util/document";

import { ATTRIBUTE_NAME } from "./utils";

export class MarkBlocksCommand extends Command {
  private markName: string;
  value = false;

  constructor(editor: Editor, markName: string) {
    super(editor);
    this.markName = markName;
  }

  refresh = () => {
    const eligible = this.getEligibleBlocksInSelection();
    this.value = eligible.selectionHasMark;
    this.isEnabled = eligible.selectedBlocks.length > 0;
  };

  execute = () => {
    const eligible = this.getEligibleBlocksInSelection();
    if (eligible.selectedBlocks.length === 0) return;

    this.editor.model.change((writer) => {
      for (const block of eligible.selectedBlocks) {
        if (eligible.selectionHasMark) {
          writer.removeAttribute(ATTRIBUTE_NAME, block);
        } else {
          writer.setAttribute(ATTRIBUTE_NAME, this.markName, block);
          for (const descendent of collectDescendents(block)) {
            if (descendent.getAttribute(ATTRIBUTE_NAME) === this.markName) {
              writer.removeAttribute(ATTRIBUTE_NAME, descendent);
              this.reconvertIfNeeded(descendent);
            }
          }
        }

        this.reconvertIfNeeded(block);
      }
    });
  };

  private reconvertIfNeeded(element: Element) {
    if (!isListItemBlock(element)) return;
    //
    // The list item downcast doesn't see changes to the mark attribute, because
    // it is based on the listItemId attribute; so it doesn't fire the editing
    // downcast if the DOM element already exists. Retrigger it manually.
    //
    this.editor.editing.reconvertItem(element);
  }

  private getEligibleBlocksInSelection() {
    const model = this.editor.model;
    const selection = model.document.selection;
    const selectedBlocks = Array.from(selection.getSelectedBlocks());

    let hasMarkedBlocks = false;
    let hasNonMarkedBlocks = false;
    let hasDescendentsOfMarkedBlocks = false;

    for (const block of selectedBlocks) {
      const currentBlockMarked =
        block.getAttribute(ATTRIBUTE_NAME) === this.markName;
      hasMarkedBlocks = hasMarkedBlocks || currentBlockMarked;
      hasNonMarkedBlocks = hasNonMarkedBlocks || !currentBlockMarked;

      for (const ancestor of collectAncestors(block)) {
        if (ancestor.getAttribute(ATTRIBUTE_NAME) === this.markName) {
          hasDescendentsOfMarkedBlocks = true;
          break;
        }
      }
    }

    if (hasMarkedBlocks && hasNonMarkedBlocks) {
      return {
        selectedBlocks: [],
        selectionHasMark: false,
      };
    }

    if (hasDescendentsOfMarkedBlocks) {
      return {
        selectedBlocks: [],
        selectionHasMark: true,
      };
    }

    return {
      selectedBlocks,
      selectionHasMark: hasMarkedBlocks,
    };
  }
}
